import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { DataLayerService } from '../data-layer/data-layer.service';

/**
 * Server-side logger. Writes entries to the shared `dbo.Logger` table via the
 * `newLog` stored procedure, using the app's existing `/api/cmd` pipeline.
 *
 * All calls are fire-and-forget — logging is best-effort and should never
 * block, delay, or fail the calling code path. If the log call itself fails
 * (offline, server down) the fallback is a console.error so the attempt isn't
 * silently dropped during local development.
 *
 * Header convention for rotation-check: "RotationCheck/<area>", e.g.
 * "RotationCheck/submit", "RotationCheck/analyze", "RotationCheck/safety".
 * Keep to ≤ 255 chars (DB column size).
 */
export type LogLevel = 'ERROR' | 'WARN' | 'INFO' | 'TRACK';

const MAX_HEADER_LEN = 255;
const MAX_MESSAGE_LEN = 100_000; // ntext is unlimited but let's not ship books

@Injectable({ providedIn: 'root' })
export class LoggerService {
  constructor(private dl: DataLayerService, private http: HttpClient) {}

  error(header: string, message: string, context?: unknown): void {
    console.error(`[${header}] ${message}`, context ?? '');
    this.write('ERROR', header, message, context);
  }

  warn(header: string, message: string, context?: unknown): void {
    console.warn(`[${header}] ${message}`, context ?? '');
    this.write('WARN', header, message, context);
  }

  info(header: string, message: string, context?: unknown): void {
    console.log(`[${header}] ${message}`, context ?? '');
    this.write('INFO', header, message, context);
  }

  track(header: string, message: string, context?: unknown): void {
    console.debug(`[${header}] ${message}`, context ?? '');
    this.write('TRACK', header, message, context);
  }

  /**
   * Sends one entry to the Logger table. Every public method also mirrors to the
   * browser console (see above) so devs see failures in DevTools without needing
   * to query the DB; callers therefore never need to pair a `console.error` next
   * to their `logger.error`.
   */
  private write(level: LogLevel, header: string, message: string, context?: unknown): void {
    try {
      const safeHeader = this.truncate(header, MAX_HEADER_LEN);
      const safeMessage = this.truncate(this.composeMessage(message, context), MAX_MESSAGE_LEN);

      // Dedicated rotation-check log endpoint (reuses the server's LogIt → newLog).
      // Not via /api/cmd, which injects @SessionID and breaks the shared newLog proc.
      this.http
        .post(`${environment.apiServer}/api/rotation-check/log`, {
          logType: level,
          header: safeHeader,
          message: safeMessage,
        })
        .subscribe({
          next: () => {},
          error: (err) => console.error('LoggerService: failed to write log entry', err),
        });
    } catch (err) {
      // Never let logging failures crash the caller.
      console.error('LoggerService: unexpected exception while logging', err);
    }
  }

  /**
   * Composes a message string with optional context appended as JSON. Handles
   * Error objects specially so the stack trace is preserved.
   */
  private composeMessage(message: string, context?: unknown): string {
    const userMeta = this.userMeta();
    let body = message;
    if (context !== undefined) {
      body += ' | ctx=' + this.stringifyContext(context);
    }
    return `${userMeta} ${body}`.trim();
  }

  private userMeta(): string {
    try {
      const u = this.dl.getUserSessionData();
      const parts: string[] = [];
      if (u?.userID != null) parts.push(`user=${u.userID}`);
      if (this.dl.sessionID) {
        parts.push(`sid=${this.dl.sessionID.slice(0, 8)}`);
      }
      return parts.length ? `[${parts.join(' ')}]` : '';
    } catch {
      return '';
    }
  }

  private stringifyContext(context: unknown): string {
    if (context === null || context === undefined) return '';
    if (context instanceof Error) {
      return JSON.stringify({ name: context.name, message: context.message, stack: context.stack });
    }
    if (typeof context === 'string') return context;
    try {
      return JSON.stringify(context);
    } catch {
      return String(context);
    }
  }

  private truncate(s: string, max: number): string {
    if (!s) return '';
    return s.length > max ? s.slice(0, max - 3) + '...' : s;
  }
}
